import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  api, ApiError, certificateUrl, certificatePdfUrl,
  type AdminCertificate, type CertificateCourseRow, type CertificateTemplateRow,
} from '../../api';

const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

export function AdminCertificates() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const search = params.get('search') ?? '';
  const standing = params.get('standing') ?? '';
  const tab = params.get('tab') ?? 'issued';

  const [rows, setRows] = useState<AdminCertificate[]>([]);
  const [total, setTotal] = useState(0);
  const [templates, setTemplates] = useState<CertificateTemplateRow[]>([]);
  const [courses, setCourses] = useState<CertificateCourseRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    const q = new URLSearchParams({ pageSize: '100' });
    if (search) q.set('search', search);
    if (standing) q.set('standing', standing);
    try {
      const [list, tpl, crs] = await Promise.all([
        api.get<{ total: number; certificates: AdminCertificate[] }>(`/admin/certificates?${q}`),
        api.get<{ templates: CertificateTemplateRow[] }>('/admin/certificates/templates'),
        api.get<{ courses: CertificateCourseRow[] }>('/admin/certificates/courses'),
      ]);
      setRows(list.certificates); setTotal(list.total);
      setTemplates(tpl.templates); setCourses(crs.courses);
    } catch (e) { setError((e as ApiError).message); }
  }, [search, standing]);
  useEffect(() => { void load(); }, [load]);

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const newTemplate = async () => {
    const name = prompt('What is this certificate called? (e.g. "Qatar Care nursing programme")');
    if (!name?.trim()) return;
    setBusy(true);
    try {
      const made = await api.post<{ id: number }>('/admin/certificates/templates', { name: name.trim() });
      navigate(`/admin/certificates/templates/${made.id}`);
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  const revoke = async (c: AdminCertificate) => {
    const reason = prompt(
      `Revoke ${c.firstName} ${c.lastName ?? ''}'s certificate for "${c.courseName}"?\n\n`
      + 'Anyone checking it will be told it has been withdrawn. Say why — it goes in the audit log.',
    );
    if (!reason?.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/certificates/${c.id}/revoke`, { reason: reason.trim() });
      setNote('Certificate revoked.'); await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  const reinstate = async (c: AdminCertificate) => {
    const reason = prompt(`Put this certificate back for ${c.firstName}? Say why.`);
    if (!reason?.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/certificates/${c.id}/reinstate`, { reason: reason.trim() });
      setNote('Certificate reinstated.'); await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  /** Two steps on purpose: count first, then issue only if the number looks right. */
  const backfill = async (course: CertificateCourseRow) => {
    setBusy(true); setError(null); setNote(null);
    try {
      const dry = await api.post<{ wouldIssue: number; message: string }>('/admin/certificates/backfill', { courseId: course.id });
      if (dry.wouldIssue === 0) { setNote(dry.message); return; }
      if (!confirm(`${dry.message}\n\nIssue ${dry.wouldIssue} certificate${dry.wouldIssue === 1 ? '' : 's'} for "${course.name}" now?`)) return;
      const out = await api.post<{ issued: number }>('/admin/certificates/backfill', { courseId: course.id, confirm: true });
      setNote(`${out.issued} certificate${out.issued === 1 ? '' : 's'} issued for "${course.name}".`);
      await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  const owed = courses.filter((c) => c.templateId && Number(c.completed) > Number(c.issued));

  return (
    <>
      <div className="page-head">
        <h1 className="h1">Certificates</h1>
        <span className="muted">{total} issued</span>
      </div>

      {note && <p className="notice">{note}</p>}
      {error && <p className="auth__error">{error}</p>}

      <div className="tabs">
        {[['issued', 'Issued'], ['courses', 'Courses'], ['templates', 'Templates']].map(([key, label]) => (
          <button key={key} className={`tabs__tab ${tab === key ? 'is-on' : ''}`} onClick={() => set('tab', key)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'issued' && (
        <>
          <div className="filters">
            <input placeholder="Search person or course" defaultValue={search}
              onKeyDown={(e) => { if (e.key === 'Enter') set('search', (e.target as HTMLInputElement).value.trim()); }}
              onBlur={(e) => set('search', e.target.value.trim())} aria-label="Search certificates" />
            <select value={standing} onChange={(e) => set('standing', e.target.value)} aria-label="Standing">
              <option value="">All</option>
              <option value="valid">Valid</option>
              <option value="expiring">Expiring within 60 days</option>
              <option value="expired">Expired</option>
              <option value="revoked">Revoked</option>
            </select>
          </div>

          <table className="grid">
            <thead>
              <tr><th>Holder</th><th>Course</th><th>Issued</th><th>Valid until</th><th>Score</th><th>Standing</th><th /></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="muted">Nothing matches.</td></tr>}
              {rows.map((c) => (
                <tr key={c.id}>
                  <td>{c.firstName} {c.lastName} <span className="muted small mono">{c.login}</span></td>
                  <td>{c.courseName}</td>
                  <td>{day(c.issuedAt)}</td>
                  <td>{c.expiresAt ? day(c.expiresAt) : <span className="muted">no expiry</span>}</td>
                  <td className="num">{c.score == null ? '' : `${Math.round(c.score)}%`}</td>
                  <td>
                    {c.standing === 'valid' && <span className="pill pill--completed">valid</span>}
                    {c.standing === 'expired' && <span className="pill pill--incomplete">expired</span>}
                    {c.standing === 'revoked' && <span className="pill pill--failed">revoked</span>}
                  </td>
                  <td className="row">
                    <a href={certificateUrl(c.publicId)} target="_blank" rel="noreferrer">open</a>
                    <a href={certificatePdfUrl(c.publicId)} target="_blank" rel="noreferrer">pdf</a>
                    {c.revokedAt
                      ? <button className="link-plain" disabled={busy} onClick={() => void reinstate(c)}>reinstate</button>
                      : <button className="link-danger" disabled={busy} onClick={() => void revoke(c)}>revoke</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {tab === 'courses' && (
        <>
          {owed.length > 0 && (
            <p className="notice">
              {owed.length} course{owed.length === 1 ? ' has' : 's have'} people who finished before Learnica
              issued certificates. “Issue the missing ones” counts them first and asks before writing anything.
            </p>
          )}
          <table className="grid">
            <thead>
              <tr><th>Course</th><th>Certificate</th><th>Valid for</th><th>Completed</th><th>Issued</th><th /></tr>
            </thead>
            <tbody>
              {courses.map((c) => (
                <tr key={c.id} className={c.status !== 'active' ? 'is-off' : ''}>
                  <td>{c.name}</td>
                  <td>{c.templateName ?? <span className="muted">none — no certificate awarded</span>}</td>
                  <td>{c.validDays ? `${c.validDays} days` : <span className="muted">no expiry</span>}</td>
                  <td className="num">{c.completed}</td>
                  <td className="num">{c.issued}</td>
                  <td>
                    {c.templateId && Number(c.completed) > Number(c.issued) && (
                      <button className="btn btn--ghost btn--small" disabled={busy} onClick={() => void backfill(c)}>
                        Issue the missing ones
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted small">
            A course awards a certificate once it has a template — set that in the course editor, under
            course settings, along with how long it stays valid.
          </p>
        </>
      )}

      {tab === 'templates' && (
        <>
          <table className="grid">
            <thead>
              <tr><th>Template</th><th>Used by</th><th>Background</th><th>As a file</th><th>Tags it uses</th><th /></tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td className="num">{t.courses}</td>
                  <td>{t.hasBackground ? 'yes' : <span className="muted">plain</span>}</td>
                  <td className="small">
                    {t.laidOut
                      ? 'PDF'
                      : <span className="warn">not laid out — printable in a browser only</span>}
                  </td>
                  <td className="small">
                    {t.tags.length === 0 ? <span className="muted">none</span> : t.tags.map((x) => `{${x}}`).join(' ')}
                    {t.unsupportedTags.length > 0 && (
                      <div className="warn">
                        Not filled in: {t.unsupportedTags.join(' ')} — these print as nothing. Tell me and I
                        will add them.
                      </div>
                    )}
                  </td>
                  <td className="row-actions">
                    <Link to={`/admin/certificates/templates/${t.id}`}>design</Link>
                    <a href={`/api/admin/certificates/templates/${t.id}/preview`} target="_blank" rel="noreferrer">
                      preview
                    </a>
                  </td>
                </tr>
              ))}
              {templates.length === 0 && <tr><td colSpan={6} className="muted">No templates.</td></tr>}
            </tbody>
          </table>
          <p>
            <button type="button" disabled={busy} onClick={() => void newTemplate()}>New template</button>
          </p>
          <p className="muted small">
            The preview uses stand-in details, so you can see exactly what a trainee will get before anyone
            earns one. A template has to be laid out before Learnica can turn it into a PDF — until then it
            can only be printed from a browser, one at a time.
          </p>
        </>
      )}
    </>
  );
}
